import { useCallback } from 'react';
import type { ArtifactType } from '@/stores/projectStore';
import { useToastStore } from '@/stores/toastStore';
import { useProcessMonitor } from '@/stores/processMonitorStore';
import { buildUserHeaders } from '@/stores/userStore';
import { API_BASE_URL } from '@/lib/config';

const ELIGIBLE_TYPES = new Set<ArtifactType>(['SKILL', 'SUBSKILL', 'SUBFLOW']);
const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 300; // ~7.5 minutes

interface StartOptions {
  artifactId: string;
  artifactName: string;
  artifactType: ArtifactType;
  projectId: string | null;
  onRefresh?: () => Promise<void> | void;
  onComplete?: () => void;
  onWarnings?: (warnings: string[]) => void;
  useFallback?: boolean;
}

interface StartResult {
  status: 'queued' | 'completed' | 'failed' | 'fallback-required';
  warnings: string[];
  fallbackReason?: string;
  fallbackMessage?: string;
  fallbackUsed?: boolean;
}

/**
 * Hook encapsulating the async LumaJSON generation workflow.
 * Handles API orchestration, process monitor integration, and polling.
 */
export function useLumaJsonGeneration() {
  const { addToast } = useToastStore();
  const { addProcess, updateProcess } = useProcessMonitor();

  const start = useCallback(async (options: StartOptions): Promise<StartResult | null> => {
    const {
      artifactId,
      artifactName,
      artifactType,
      projectId,
      onRefresh,
      onComplete,
      onWarnings
    } = options;

    let response: Response;

    try {
      response = await fetch(`${API_BASE_URL}/artifacts/${artifactId}/generate-lumajson`, {
        method: 'POST',
        headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ useFallback: Boolean(options.useFallback) })
      });
    } catch (error) {
      console.error('[LumaJSON] Network error while starting generation', error);
      addToast('error', 'Failed to contact API for LumaJSON generation.');
      return null;
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      if (response.status === 409 && data?.fallbackAvailable) {
        return {
          status: 'fallback-required',
          warnings: [],
          fallbackReason: data.fallbackReason,
          fallbackMessage: data.error || data.message || 'The LLM output could not be validated against the LumaJSON schema.'
        };
      }

      const message =
        data?.message ||
        data?.details ||
        data?.error ||
        (response.status === 503
          ? 'Unable to reach the LLM provider. Check network access or try again later.'
          : 'Failed to start LumaJSON generation.');
      addToast('error', message);
      return null;
    }

    // Deterministic fallback (attributes/integrations/etc.)
    if (!ELIGIBLE_TYPES.has(artifactType)) {
      addToast('success', 'LumaJSON generated successfully.');
      await onRefresh?.();
      onComplete?.();
      return { status: 'completed', warnings: [] };
    }

    // Local dev path returns completed payload immediately
    if (!data.jobId) {
      const warnings: string[] = Array.isArray(data.warnings) ? data.warnings : [];
      const fallbackUsed = Boolean(data.fallbackUsed);
      await onRefresh?.();
      if (warnings.length > 0) {
        warnings.forEach((warning) => addToast('warning', warning));
        onWarnings?.(warnings);
      } else {
        addToast('success', fallbackUsed ? 'LumaJSON generated using deterministic fallback.' : 'LumaJSON generated successfully.');
      }
      onComplete?.();
      return { status: data.status || 'completed', warnings, fallbackUsed };
    }

    const jobId: string = data.jobId;

    addProcess({
      id: jobId,
      type: 'lumajson',
      skillId: artifactId,
      skillName: artifactName,
      startedAt: Date.now(),
      status: 'queued',
      modelInfo: undefined
    });

    let polls = 0;

    while (polls < MAX_POLLS) {
      polls += 1;
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      let statusResponse: Response;
      try {
        statusResponse = await fetch(`${API_BASE_URL}/jobs/lumajson/${jobId}`, {
          headers: buildUserHeaders()
        });
      } catch (error) {
        console.error('[LumaJSON] Job polling failed', error);
        updateProcess(jobId, { status: 'failed', error: 'Network error while polling job status' });
        addToast('error', 'Lost connection while polling LumaJSON job status.');
        return { status: 'failed', warnings: [] };
      }

      if (!statusResponse.ok) {
        const message = await statusResponse.text();
        console.error('[LumaJSON] Polling API returned error', message);
        updateProcess(jobId, { status: 'failed', error: 'Failed to poll job status' });
        addToast('error', 'Failed to poll LumaJSON job status.');
        return { status: 'failed', warnings: [] };
      }

      const job = await statusResponse.json();

      if (job.status === 'processing') {
        updateProcess(jobId, { status: 'processing' });
      }

      if (job.status === 'completed') {
        const warnings: string[] = Array.isArray(job.warnings) ? job.warnings : [];
        updateProcess(jobId, { status: 'completed', warnings });

        await onRefresh?.();
        if (warnings.length > 0) {
          warnings.forEach((warning: string) => addToast('warning', warning));
          onWarnings?.(warnings);
        } else {
          addToast('success', 'LumaJSON generated successfully.');
        }

        onComplete?.();
        return { status: 'completed', warnings, fallbackUsed: Boolean(job.fallbackUsed) };
      }

      if (job.status === 'failed') {
        const errorMessage = job.error || job.message || 'LumaJSON generation failed.';
        const isNetworkIssue = /fetch failed|timeout|ecconn|enotfound|network|unable to reach/i.test(errorMessage.toLowerCase());
        if (job.fallbackAvailable) {
          updateProcess(jobId, { status: 'failed', error: errorMessage });
          return {
            status: 'fallback-required',
            warnings: Array.isArray(job.warnings) ? job.warnings : [],
            fallbackReason: job.fallbackReason,
            fallbackMessage: errorMessage
          };
        }
        updateProcess(jobId, { status: 'failed', error: errorMessage });
        addToast(isNetworkIssue ? 'warning' : 'error', errorMessage);
        return { status: 'failed', warnings: Array.isArray(job.warnings) ? job.warnings : [] };
      }
    }

    updateProcess(jobId, { status: 'timeout', error: 'Timed out while waiting for LumaJSON generation.' });
    addToast('error', 'Timed out while waiting for LumaJSON generation.');
    return { status: 'failed', warnings: [] };
  }, [addProcess, addToast, updateProcess]);

  return { startLumaJsonGeneration: start };
}
